/**
 * TanStack Query client + defaults for Altiv.
 *
 * Defaults reasoning (Sprint 2.2 Fase 4):
 * - staleTime 30s: balance between not spamming the backend on rapid nav
 *   and not showing stale data in an operational app. Catalog-style queries
 *   (airports, pilot lists) override to 10min via their own useQuery options.
 * - gcTime 5min: keep unmounted query data around briefly so back-nav is
 *   instant without re-fetching.
 * - retry: 2 attempts, ONLY on 5xx. 4xx errors are real (validation, auth,
 *   not-found) — retrying them spams the backend and delays the visible
 *   error.
 * - refetchOnWindowFocus: true. The app is operational; if the user comes
 *   back from a tab/app switch we want a fresh read.
 * - refetchOnReconnect: true. Same reason — if network blipped, refresh.
 *
 * Mutations don't default to optimistic — aviation context favors "saving..."
 * + real result over "looks done" + reverse-if-fails. Per-mutation opt-in
 * for chat / notification toggles where speed matters more than certainty.
 *
 * Mutations use networkMode "always" (not the RQ default "online") because the
 * offline write path lives in the axios interceptor, not in React Query — see
 * the `mutations` block below.
 */

import { QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  const ax = error as AxiosError | undefined;
  const status = ax?.response?.status;
  if (!status) return true;
  return status >= 500;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: shouldRetry,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
      // The offline write path lives in the axios interceptor (src/lib/api.ts →
      // offline-outbox): queueable mutations are persisted to IndexedDB and
      // replayed on reconnect. React Query's default networkMode "online" PAUSES
      // a mutation while offline, so mutationFn (the api.post) never runs and
      // nothing reaches the interceptor — silently defeating the outbox (the
      // indicator hangs on "saving" and the queue stays empty). "always" lets
      // every write hit the interceptor: whitelisted ones queue (202 → replay
      // on reconnect), the rest reject with a network error for the UI to show.
      networkMode: "always",
    },
  },
});

/**
 * Stale-while-revalidate fetch.
 *
 * Returns cached data immediately when present (with `stale: true` when older
 * than `ttl`) and kicks off a background refresh. Cold miss awaits the
 * fetcher. Same semantics the old `dataCache.fetch()` shim exposed — kept as
 * a standalone helper so imperative call sites (WS handlers, useState
 * initializers, useEffect-driven sequences) don't have to re-inline the
 * branch logic. Hook-driven reads should use `useQuery` instead.
 */
const DEFAULT_SWR_TTL = 5 * 60 * 1000;
export async function fetchSWR<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = DEFAULT_SWR_TTL
): Promise<{ data: T | undefined; stale: boolean }> {
  const queryKey = [key];
  const state = queryClient.getQueryState<T>(queryKey);
  const hasData = state?.data !== undefined;
  const age = state?.dataUpdatedAt ? Date.now() - state.dataUpdatedAt : Infinity;
  // `isInvalidated` is set by queryClient.invalidateQueries() — the mechanism
  // every cacheInvalidators.js helper uses after a mutation. Without honoring
  // it here, fetchSWR would keep serving the pre-mutation snapshot for the full
  // TTL even though a mutation already marked it stale (e.g. approving a trip in
  // the OCC, then reopening the FlightDetailModal within 5 min still showed
  // "pending"). Treat an invalidated entry as stale so SWR revalidates.
  const fresh = hasData && age < ttl && !state?.isInvalidated;

  if (hasData && fresh) return { data: state!.data, stale: false };

  // Explicitly invalidated data is known-wrong (a mutation just touched it), not
  // merely old. Returning it via the SWR branch below would hand the caller a
  // stale snapshot synchronously and only refresh the cache in the background —
  // useless for one-shot consumers (FlightDetailModal awaits the result once).
  // Block and await fresh data instead, like a cold miss.
  if (hasData && state?.isInvalidated) {
    const data = await queryClient.fetchQuery({ queryKey, queryFn: fetcher, staleTime: 0 });
    return { data, stale: false };
  }

  if (hasData) {
    // Stale-while-revalidate — return stale immediately, refresh in background.
    queryClient.fetchQuery({ queryKey, queryFn: fetcher, staleTime: 0 }).catch(() => {});
    return { data: state!.data, stale: true };
  }

  const data = await queryClient.fetchQuery({ queryKey, queryFn: fetcher, staleTime: ttl });
  return { data, stale: false };
}

/**
 * Subscribe to cache data transitions for a single string key. Returns an
 * unsubscribe function. Fires only when `data` actually changes (skips pure
 * status flips like fetching→idle). Same semantics as the old
 * `dataCache.subscribe()` shim. For React-render flows, prefer `useQuery`.
 */
export function subscribeQueryKey<T>(key: string, cb: (_data: T) => void): () => void {
  let lastData = queryClient.getQueryData<T>([key]);
  return queryClient.getQueryCache().subscribe((event) => {
    const eventKey = event?.query?.queryKey?.[0];
    if (eventKey !== key) return;
    const data = event.query.state.data as T | undefined;
    if (data !== undefined && data !== lastData) {
      lastData = data;
      cb(data);
    }
  });
}

/**
 * Invalidate every query whose first key element string starts with `prefix`.
 * Mirrors the legacy `startsWith` semantics for callers keying by
 * URL-with-querystring (e.g. `/calendar?month=2026-05` matches `/calendar`).
 */
export function invalidateByPrefix(prefix: string): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === "string" && (query.queryKey[0] as string).startsWith(prefix),
  });
}

// Stale times for query categories that diverge from the 30s default.
// Use as `useQuery({ ..., staleTime: STALE_TIME.CATALOG })`.
export const STALE_TIME = {
  /** Catalog data that almost never changes within a session (airports, regions, fixed lists). */
  CATALOG: 10 * 60 * 1000,
  /** Operational data — trips, legs, status. The 30s default. */
  OPERATIONAL: 30 * 1000,
  /** Real-time feeds (live tracking positions). Override usually with WS-driven invalidation instead. */
  REALTIME: 5 * 1000,
} as const;
